/**
 * Cliente del print bridge HTTP local.
 *
 * Conecta con el servicio `print-bridge` que corre en la PC del kiosko
 * (puerto 9101). Permite que el POS web imprima tickets sin necesidad
 * de WebUSB, drivers o Tauri.
 *
 * El bridge soporta impresoras USB, red y LPT. Si no está corriendo,
 * el POS sigue funcionando en modo "vista previa" (toast con el ticket).
 */

export interface VentaItem {
  nombre: string;
  cantidad: number;
  precio_unit: number;
  subtotal: number;
}

export interface Venta {
  numero: string | number;
  fecha?: string;
  cajero?: string;
  cliente?: string | null;
  items: VentaItem[];
  total: number;
  pagos?: { medio: string; monto: number }[];
  vuelto?: number;
}

const BRIDGE_URL =
  (import.meta as { env: { VITE_BRIDGE_URL?: string } }).env.VITE_BRIDGE_URL ||
  "http://localhost:9101";

const BRIDGE_TOKEN =
  (import.meta as { env: { VITE_BRIDGE_TOKEN?: string } }).env.VITE_BRIDGE_TOKEN ||
  "";

export interface BridgeStatus {
  available: boolean;
  version?: string;
  printers?: string[];
  error?: string;
}

let cachedStatus: BridgeStatus | null = null;
let cachedAt = 0;

/**
 * Pinguea el bridge local. Cachea resultado por 30s para no spamearlo.
 */
export async function checkBridge(force = false): Promise<BridgeStatus> {
  const now = Date.now();
  if (!force && cachedStatus && now - cachedAt < 30_000) return cachedStatus;

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2000);
    const r = await fetch(`${BRIDGE_URL}/health`, { signal: controller.signal });
    clearTimeout(timeout);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const data = (await r.json()) as {
      status: string;
      version: string;
      printers: string[];
    };
    cachedStatus = {
      available: data.status === "ok",
      version: data.version,
      printers: data.printers,
    };
  } catch (e) {
    cachedStatus = {
      available: false,
      error: e instanceof Error ? e.message : "unknown",
    };
  }
  cachedAt = now;
  return cachedStatus;
}

interface PrintRequest {
  printer?: string;
  type?: "ticket" | "text" | "escpos";
  content: unknown;
  cut?: boolean;
  open_drawer?: boolean;
}

async function bridgePost(
  path: string,
  body: PrintRequest
): Promise<{ ok: boolean; error?: string }> {
  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (BRIDGE_TOKEN) headers["X-Bridge-Token"] = BRIDGE_TOKEN;

    const r = await fetch(`${BRIDGE_URL}${path}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });
    const data = (await r.json()) as { ok?: boolean; error?: string };
    if (!r.ok || data.error) {
      return { ok: false, error: data.error || `HTTP ${r.status}` };
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : "network error" };
  }
}

/**
 * Imprime una venta usando el formato estructurado (más prolijo que texto plano).
 * Si el bridge no está disponible, devuelve { ok: false, error: ... } y el POS
 * puede mostrar un toast.
 */
export async function printTicket(
  v: Venta,
  printer = "default"
): Promise<{ ok: boolean; error?: string }> {
  return bridgePost("/print", {
    printer,
    type: "ticket",
    content: {
      header: ["MAXIKIOSKO JL", "Los Condores 460", "V. Carlos Paz", "Tel: (3541) 420532"],
      subheader: [
        `Ticket #${v.numero}`,
        v.fecha ? `Fecha: ${v.fecha}` : "",
        v.cajero ? `Cajero: ${v.cajero}` : "",
        v.cliente ? `Cliente: ${v.cliente}` : "",
      ].filter(Boolean),
      items: v.items.map((it) => ({
        qty: it.cantidad,
        name: it.nombre,
        price: it.precio_unit,
      })),
      total: v.total,
      payments: v.pagos?.map((p) => ({ method: p.medio, amount: p.monto })) || [],
      change: v.vuelto || 0,
      footer: ["", "Gracias por su compra"],
    },
    cut: true,
    open_drawer: false,
  });
}

/**
 * Imprime un texto plano (debug o ticket no estructurado).
 */
export async function printText(text: string, printer = "default") {
  return bridgePost("/print", { printer, type: "text", content: text, cut: true });
}

/**
 * Imprime un ticket de prueba (delegado al bridge).
 */
export async function printTest(printer = "default") {
  return bridgePost("/test", { printer, content: {} });
}
